import { CustomizeCheckoutData } from '@/@types/Checkouts/edit-checkout'
import { AxiosErrorResponse } from '@/@types/Errors'
import { useAuth } from '@/auth/Auth'
import Loader from '@/components/Loader/Loader'
import { useFetchProductById } from '@/hooks/useFetchProductById'
import { useProductPhotoPreviewUrl } from '@/hooks/usePreviewUrl'
import { toast } from '@/hooks/use-toast'
import LayoutInside from '@/layout/LayoutInside'
import { CheckoutsService } from '@/services/checkouts.service'
import {
  findFirstMissingBannerImage,
  getCustomizeCheckoutErrorMessage,
  getCustomizeCheckoutFieldErrorMessage,
  prepareCustomizeCheckoutPayload,
  resolveShowWhatsAppSupport,
} from '@/utils/customizeCheckout'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { buildCustomizeCheckoutSchema } from '@/utils/buildCustomizeCheckoutSchema'
import { useWideCheckoutPreview } from './CheckoutLivePreview'
import { bannerFieldsFromCheckout } from './checkoutFormPatch'
import { CheckoutTemplateId, isCheckoutTemplate } from '@/utils/checkoutTemplates'
import { PersonalizeCheckoutScreen } from './PersonalizeCheckoutScreen'

export default function PersonalizeCheckout() {
  const [activeTab, setActiveTab] = useState('layout')
  const [previewReload, setPreviewReload] = useState(0)
  const [previewOpen, setPreviewOpen] = useState(false)
  const widePreview = useWideCheckoutPreview()
  const { t } = useTranslation(['common', 'checkouts', 'sales'])
  const { id, checkoutId } = useParams<{ id: string; checkoutId: string }>()
  const { authData } = useAuth()
  const queryClient = useQueryClient()

  const {
    data: checkout,
    isPending: isLoadingCheckout,
    isError: isCheckoutError,
  } = useQuery({
    queryFn: () =>
      CheckoutsService.getCheckout({
        productId: id ?? '',
        checkoutId: checkoutId ?? '',
      }),
    queryKey: [`checkouts-${id}`, checkoutId],
    enabled: Boolean(id && checkoutId),
  })

  const breadcrumbItems = [
    { text: t('common:products'), link: '/products' },
    {
      text: t('common:edit_product'),
      link: `/products/edit-product/${id}/checkouts`,
    },
    {
      text: t('checkouts:personalize_checkout'),
      link: `/products/edit-product/${id}/checkouts/${checkoutId}`,
    },
  ]

  const EditCheckoutSchema = buildCustomizeCheckoutSchema(t)


  const {
    watch,
    register,
    formState: { errors },
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useForm<CustomizeCheckoutData>({
    resolver: zodResolver(EditCheckoutSchema),
    defaultValues: {
      name: checkout?.name ?? '',
      timer_in_minutes: checkout?.timer_in_minutes ?? 0,
      show_notification: checkout?.show_notification ?? false,
      show_whatsapp_support: resolveShowWhatsAppSupport(checkout),
      whatsapp_support: (checkout as any)?.whatsapp_support ?? null,
      phone_prefix: (checkout as any)?.phone_prefix ?? null,
      urgency_description: checkout?.urgency_description ?? '',
      urgency_background_color: checkout?.urgency_background_color ?? '#a1a1a9',
      urgency_text_color: checkout?.urgency_text_color ?? '#ffffff',
      show_urgency: checkout?.show_urgency ?? false,
      banner_mobile_top_image: null,
      banner_mobile_bottom_image: null,
      banner_desktop_top_image: null,
      banner_desktop_bottom_image: null,
      banner_desktop_side_image: null,
      banner_desktop_bottom_is_visible: '0',
      banner_desktop_side_is_visible: '0',
      banner_mobile_top_is_visible: '0',
      banner_mobile_bottom_is_visible: '0',
      default_payment_method:
        checkout?.default_payment_method?.type ?? 'credit_card',
      template: isCheckoutTemplate(checkout?.template)
        ? checkout.template
        : undefined,
      product_photo: checkout?.product_photo_url ?? null,
      remove_product_photo: false,
      ...bannerFieldsFromCheckout(checkout?.banners),
    },
  })

  const defaultValues = watch()
  const serverTemplate: CheckoutTemplateId = isCheckoutTemplate(checkout?.template)
    ? checkout.template
    : 'default'
  const [trackedCheckoutId, setTrackedCheckoutId] = useState(checkoutId)
  const [chosenTemplate, setChosenTemplate] = useState<CheckoutTemplateId | null>(null)
  if (checkoutId !== trackedCheckoutId) {
    setTrackedCheckoutId(checkoutId)
    setChosenTemplate(null)
  }
  const templateForUi = chosenTemplate ?? serverTemplate
  const hydratedCheckoutId = useRef<string | null>(null)
  const { product } = useFetchProductById(id || '')
  const offerPhotoPreviewUrl = useProductPhotoPreviewUrl(
    defaultValues.remove_product_photo ? null : defaultValues.product_photo,
  )
  const productImageUrl = product?.images?.[0]?.url ?? null
  const productPhotoPreviewUrl = offerPhotoPreviewUrl || productImageUrl

  useEffect(() => {
    if (!checkout || !checkoutId) return
    if (hydratedCheckoutId.current === checkoutId) return
    hydratedCheckoutId.current = checkoutId

    setValue('timer_in_minutes', checkout.timer_in_minutes)
    setValue('show_notification', checkout.show_notification)
    setValue('show_whatsapp_support', resolveShowWhatsAppSupport(checkout))
    setValue('whatsapp_support', (checkout as any)?.whatsapp_support ?? null)
    setValue('phone_prefix', (checkout as any)?.phone_prefix ?? null)
    setValue('urgency_description', checkout.urgency_description)
    setValue(
      'urgency_background_color',
      checkout.urgency_background_color || '#a1a1a9',
    )
    setValue('urgency_text_color', checkout.urgency_text_color || '#ffffff')
    setValue('name', checkout.name)
    setValue('show_urgency', checkout.show_urgency ?? false)
    setValue(
      'default_payment_method',
      checkout.default_payment_method?.type || 'credit_card',
    )
    setValue(
      'template',
      isCheckoutTemplate(checkout.template) ? checkout.template : 'default',
    )

    const currentPhoto = watch('product_photo')
    const isRemovingPhoto = watch('remove_product_photo')

    if (!(currentPhoto instanceof File) && !isRemovingPhoto) {
      setValue('product_photo', checkout.product_photo_url ?? null)
      setValue('remove_product_photo', false)
    }

    const bannerPatch = bannerFieldsFromCheckout(checkout.banners)
    ;(Object.keys(bannerPatch) as (keyof CustomizeCheckoutData)[]).forEach(
      (key) => {
        setValue(key, bannerPatch[key] as never)
      },
    )
  }, [checkout, checkoutId, setValue, watch])

  const { mutate, isPending: isEditingCheckout } = useMutation({
    mutationFn: (data: CustomizeCheckoutData) =>
      CheckoutsService.customizeCheckout({
        data,
        productId: id ?? '',
        checkoutId: checkoutId ?? '',
      }),
    onSuccess: (updatedCheckout) => {
      queryClient.setQueryData([`checkouts-${id}`, checkoutId], updatedCheckout)
      queryClient.invalidateQueries({ queryKey: [`checkouts-${id}`] })
      setValue('product_photo', updatedCheckout?.product_photo_url ?? null)
      setValue('remove_product_photo', false)
      if (isCheckoutTemplate(updatedCheckout?.template)) {
        setChosenTemplate(updatedCheckout.template)
        setValue('template', updatedCheckout.template)
      }
      setPreviewReload((token) => token + 1)
      toast({
        variant: 'success',
        description: t('checkouts:checkout_saved'),
      })
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      const status = error.response?.status
      const responseData = error.response?.data

      const fieldErrors = responseData?.errors
      if (status === 422 && fieldErrors) {
        Object.keys(fieldErrors).forEach((key) => {
          setError(key as keyof CustomizeCheckoutData, {
            type: 'required',
            message: getCustomizeCheckoutFieldErrorMessage(
              key,
              fieldErrors[key][0],
              t,
            ),
          })
        })
      }

      toast({
        variant: 'error',
        description: getCustomizeCheckoutErrorMessage(error, t),
      })
    },
  })

  const chooseTemplate = (next: CheckoutTemplateId) => {
    setChosenTemplate(next)
    setValue('template', next, { shouldDirty: true, shouldTouch: true })
  }

  const onSubmit = async (data: CustomizeCheckoutData) => {
    const isFile = (file: any) => file instanceof File
    const isWhatsAppEnabled = Boolean(data.show_whatsapp_support)

    if (
      data.show_urgency &&
      (data.timer_in_minutes == null || data.timer_in_minutes < 1)
    ) {
      const message = t('checkouts:timer_min_one_minute')
      setError('timer_in_minutes', {
        type: 'min',
        message,
      })
      setActiveTab('urgency')
      toast({
        variant: 'error',
        description: message,
      })
      return
    }

    if ((data.urgency_description ?? '').length > 255) {
      const message = t('common:validation.maxLength', { max: 255 })
      setError('urgency_description', {
        type: 'maxLength',
        message,
      })
      setActiveTab('urgency')
      toast({
        variant: 'error',
        description: message,
      })
      return
    }

    if (isWhatsAppEnabled) {
      const failWhatsApp = (message: string) => {
        setError('whatsapp_support' as keyof CustomizeCheckoutData, {
          type: 'required',
          message,
        })
        setActiveTab('urgency')
        toast({
          variant: 'error',
          description: message,
        })
      }

      const supportValue = (data.whatsapp_support ?? '').toString()

      if (!supportValue.trim()) {
        failWhatsApp(t('validationWhatsApp'))
        return
      }

      const digitsOnly = supportValue.replace(/\D/g, '')

      if (digitsOnly.length < 9) {
        failWhatsApp(t('validationWhatsAppMinLength'))
        return
      }

      if (digitsOnly.length > 15) {
        failWhatsApp(t('validationInvalidWhatsApp'))
        return
      }
    }

    const missingBanner = findFirstMissingBannerImage(data)
    if (missingBanner) {
      setError(missingBanner.image as keyof CustomizeCheckoutData, {
        type: 'required',
        message: t('checkouts:banner_image_required'),
      })
      setActiveTab(missingBanner.tab)
      toast({
        variant: 'error',
        description: t('checkouts:banner_image_required'),
      })
      return
    }

    const payload = prepareCustomizeCheckoutPayload({
      ...data,
      name: checkout?.name ?? data.name,
      template: templateForUi,
    })

    if (payload.remove_product_photo) {
      delete payload.product_photo
    } else if (!isFile(payload.product_photo)) {
      delete payload.product_photo
      delete payload.remove_product_photo
    } else {
      delete payload.remove_product_photo
    }

    mutate(payload)
  }

  return (
    <LayoutInside items={breadcrumbItems}>
      {isLoadingCheckout ? (
        <div className="flex items-center justify-center h-[calc(100vh-120px)]">
          <Loader />
        </div>
      ) : isCheckoutError || !checkout || !checkoutId ? (
        <div className="flex items-center justify-center h-[calc(100vh-120px)] text-muted">
          {t('common:data_submission_error')}
        </div>
      ) : (
        <PersonalizeCheckoutScreen
          checkoutName={checkout.name}
          checkoutId={checkoutId}
          checkoutBaseUrl={checkout.checkout_base_url}
          template={templateForUi}
          onTemplateChange={chooseTemplate}
          previewReload={previewReload}
          widePreview={widePreview}
          previewOpen={previewOpen}
          onPreviewOpenChange={setPreviewOpen}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          values={defaultValues}
          setValue={setValue}
          register={register}
          errors={errors}
          clearErrors={clearErrors}
          watch={watch}
          productPhotoPreviewUrl={productPhotoPreviewUrl}
          checkoutPrice={checkout.offer?.price_formatted}
          paymentMethods={checkout.offer?.paymentMethods}
          availablePaymentMethods={authData?.available_payment_methods}
          isSaving={isEditingCheckout}
          onSave={() => onSubmit({ ...getValues(), template: templateForUi })}
        />
      )}
    </LayoutInside>
  )
}
